import { memo, ReactElement } from 'react'
import { CoverImage } from './CoverImage'
import { ProfileImage } from './ProfileImage'
import Styles from './UserProfileCard.module.scss'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezonePlugin from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import cx from 'classnames'
import { MemberProfile } from 'types'
import { Card } from './Card'
import { LocalisedCurrentTime } from '../LocalisedCurrentTime/LocalisedCurrentTime'

dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timezonePlugin)

export interface Props {
  profile: MemberProfile
  isMe?: boolean
  isHovered?: boolean
  reduced?: boolean
}

const UserProfileCard = ({ profile, reduced = false, isMe = false, isHovered = false }: Props) => {
  const {
    id,
    avatar_url: avatarUrl,
    profile_image_url: profileImageUrl,
    cover_image_url: coverImageUrl,
    display_name: displayName,
    current_status_icon: currentStatusIcon,
    current_status: currentStatus,
    pronouns,
    role,
    email,
    location,
    timezone,
  } = profile

  let roleDisplay = role ? role : email

  if (roleDisplay && pronouns) {
    roleDisplay = `${roleDisplay} • ${pronouns}`
  }

  let locationDisplay: string | ReactElement | null = location

  if (locationDisplay && timezone) {
    locationDisplay = (
      <>
        {location} • <LocalisedCurrentTime timezone={timezone} />
      </>
    )
  }

  const currentStatusDisplay = `${currentStatusIcon || ''} ${currentStatus || ''}`

  return (
    <Card
      coverImage={
        <CoverImage
          coverImageUrl={coverImageUrl}
          profileImageUrl={profileImageUrl || avatarUrl}
          userId={id}
          alt={displayName}
        />
      }
      profileImage={
        <div className={cx(Styles.profileImageInner)}>
          <ProfileImage profileImageUrl={profileImageUrl || avatarUrl} alt={displayName} userId={id} />
        </div>
      }
      className={cx(Styles.UserProfileCard, { [Styles.enlarged]: isHovered })}
    >
      <div className={Styles.contentWrapper}>
        <div className={Styles.info}>
          <h4 className={Styles.name}>
            {displayName}
            {isMe ? <span className={Styles.isMe}> You</span> : ''}
          </h4>
          {roleDisplay && roleDisplay.length > 0 && <p className={Styles.role}>{roleDisplay}</p>}
          {currentStatusDisplay.trim().length > 0 && <p className={Styles.status}>{currentStatusDisplay}</p>}
        </div>
        {locationDisplay && !reduced && <p className={Styles.location}>{locationDisplay}</p>}
      </div>
    </Card>
  )
}

const arePropsEqual = (previousProps: Props, nextProps: Props) =>
  JSON.stringify(previousProps) === JSON.stringify(nextProps)

const MemoizedUserProfileCard = memo(UserProfileCard, arePropsEqual)

export { MemoizedUserProfileCard as UserProfileCard }
